<template>
  <div v-show="isEnabled" class="trend-storyteller-shell">
    <button
      class="trend-storyteller-trigger"
      type="button"
      aria-label="Open Trend Storyteller"
      @click="toggleOpen"
    >
      <span class="trend-storyteller-trigger-icon">TS</span>
      <span class="trend-storyteller-trigger-text">Trend Storyteller</span>
    </button>

    <transition name="trend-storyteller-fade">
      <section v-if="isOpen" class="trend-storyteller-panel">
        <div class="trend-storyteller-header">
          <div>
            <p class="trend-storyteller-label">Insight Plugin</p>
            <h3>Trend Storyteller</h3>
            <p class="trend-storyteller-subtitle">
              Select a dashboard indicator and location to generate a trend summary from live periods and values.
            </p>
          </div>
          <button
            class="trend-storyteller-close"
            type="button"
            aria-label="Close Trend Storyteller"
            @click="isOpen = false"
          >
            <svg viewBox="0 0 20 20" aria-hidden="true" class="trend-storyteller-close-icon">
              <path d="M5.5 5.5L14.5 14.5M14.5 5.5L5.5 14.5" />
            </svg>
          </button>
        </div>

        <div class="trend-storyteller-grid">
          <label class="trend-storyteller-field">
            <span>Indicator</span>
            <select v-model.number="form.selectedIndicatorId" :disabled="isLoadingOptions" @change="handleIndicatorChange">
              <option :value="null" disabled>Select indicator</option>
              <option v-for="indicator in indicators" :key="indicator.id" :value="indicator.id">
                {{ indicator.short_name || indicator.full_name || indicator.name }}
              </option>
            </select>
          </label>

          <label class="trend-storyteller-field">
            <span>Location</span>
            <select v-model.number="form.selectedLocationId" :disabled="isLoadingOptions" @change="handleLocationChange">
              <option :value="null" disabled>Select location</option>
              <option v-for="location in locations" :key="location.id" :value="location.id">
                {{ location.name }}
              </option>
            </select>
          </label>

          <label class="trend-storyteller-field">
            <span>Audience</span>
            <select v-model="form.audience">
              <option value="analyst">Analyst</option>
              <option value="executive">Executive</option>
              <option value="public">Public</option>
            </select>
          </label>

          <label class="trend-storyteller-field">
            <span>Datasource</span>
            <select
              v-model.number="form.selectedDatasourceId"
              :disabled="isLoadingOptions || !availableDatasources.length"
              @change="handleDatasourceChange"
            >
              <option :value="null" disabled>Select datasource</option>
              <option
                v-for="datasource in availableDatasources"
                :key="datasource.id"
                :value="datasource.id"
              >
                {{ datasource.name }}
              </option>
            </select>
          </label>
        </div>

        <div class="trend-storyteller-status">
          <div class="trend-storyteller-chip muted">
            Periods: {{ availablePeriods.length }}
          </div>
          <div class="trend-storyteller-chip muted">
            Values loaded: {{ seriesValues.length }}
          </div>
        </div>

        <div class="trend-storyteller-grid trend-storyteller-preview-grid">
          <label class="trend-storyteller-field trend-storyteller-field-wide">
            <span>Periods</span>
            <textarea :value="availablePeriods.join(', ')" rows="3" disabled></textarea>
          </label>
          <label class="trend-storyteller-field trend-storyteller-field-wide">
            <span>Values</span>
            <textarea :value="seriesValues.join(', ')" rows="3" disabled></textarea>
          </label>
        </div>

        <div class="trend-storyteller-actions">
          <button class="trend-storyteller-btn secondary" type="button" :disabled="isLoadingData" @click="refreshSeries">
            Refresh values
          </button>
          <button class="trend-storyteller-btn secondary" type="button" @click="resetResult">
            Clear result
          </button>
          <button
            class="trend-storyteller-btn"
            type="button"
            :disabled="isLoadingData || !canGenerateStory"
            @click="generateStory"
          >
            {{ isLoadingData ? 'Loading…' : 'Tell the story' }}
          </button>
        </div>

        <p v-if="error" class="trend-storyteller-error">{{ error }}</p>

        <div v-if="result" class="trend-storyteller-result">
          <div class="trend-storyteller-hero">
            <p class="trend-storyteller-result-label">Headline</p>
            <h4>{{ result.headline }}</h4>
            <p class="trend-storyteller-result-text">{{ result.keyTakeaway }}</p>
          </div>

          <div class="trend-storyteller-insight-grid">
            <article
              v-for="card in result.insightCards"
              :key="card.title"
              class="trend-storyteller-insight-card"
            >
              <p class="trend-storyteller-result-label">{{ card.title }}</p>
              <p class="trend-storyteller-insight-value">{{ card.value }}</p>
              <p class="trend-storyteller-insight-copy">{{ card.copy }}</p>
            </article>
          </div>

          <div class="trend-storyteller-result-block">
            <p class="trend-storyteller-result-label">Summary</p>
            <p class="trend-storyteller-result-text">{{ result.summary }}</p>
          </div>
          <div class="trend-storyteller-result-block">
            <p class="trend-storyteller-result-label">Highlights</p>
            <ul class="trend-storyteller-highlights">
              <li v-for="(highlight, index) in result.highlights" :key="`${index}-${highlight}`">
                {{ highlight }}
              </li>
            </ul>
          </div>
          <div class="trend-storyteller-result-block">
            <p class="trend-storyteller-result-label">What stands out</p>
            <ul class="trend-storyteller-highlights">
              <li>{{ result.recentMomentum }}</li>
              <li>{{ result.consistencySignal }}</li>
              <li>{{ result.watchout }}</li>
            </ul>
          </div>
          <div class="trend-storyteller-result-inline">
            <div class="trend-storyteller-chip">{{ result.trendLabel }}</div>
            <div class="trend-storyteller-chip">{{ result.performanceBand }}</div>
            <div class="trend-storyteller-chip muted">{{ result.caption }}</div>
          </div>
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
import ApiServices from '@/modules/data-layer/services/ApiServices';

export default {
  name: 'TrendStorytellerPlugin',
  data() {
    return {
      isEnabled: true,
      isOpen: false,
      isLoadingOptions: false,
      isLoadingData: false,
      error: '',
      result: null,
      indicators: [],
      locations: [],
      datasourceCatalog: [],
      availablePeriods: [],
      seriesValues: [],
      form: {
        selectedIndicatorId: null,
        selectedLocationId: null,
        selectedDatasourceId: null,
        audience: 'analyst',
      },
    };
  },
  computed: {
    dashboardId() {
      return Number(
        this.$store.state?.MSDAT_STORE?.configObject?.id
        || localStorage.getItem('activeDashboardID')
        || 0,
      ) || null;
    },
    defaultLocationId() {
      return Number(this.$store.state?.MSDAT_STORE?.default?.location || 1);
    },
    selectedIndicator() {
      return this.indicators.find((indicator) => indicator.id === this.form.selectedIndicatorId) || null;
    },
    selectedLocation() {
      return this.locations.find((location) => location.id === this.form.selectedLocationId) || null;
    },
    availableDatasources() {
      const datasources = this.selectedIndicator?.datasources;
      if (!Array.isArray(datasources) || !datasources.length) {
        return [];
      }

      return datasources
        .map((datasource) => {
          if (typeof datasource === 'object') {
            const id = Number(datasource.id || datasource.datasource || 0) || null;
            if (!id) return null;

            return {
              id,
              name: this.getDatasourceDisplayName(datasource)
                || this.getDatasourceDisplayName(this.datasourceCatalog.find((item) => item.id === id))
                || `Datasource ${id}`,
            };
          }

          const id = Number(datasource) || null;
          if (!id) return null;

          return {
            id,
            name: this.getDatasourceDisplayName(this.datasourceCatalog.find((item) => item.id === id))
              || `Datasource ${id}`,
          };
        })
        .filter(Boolean);
    },
    selectedDatasourceId() {
      const currentId = Number(this.form.selectedDatasourceId) || null;
      if (currentId && this.availableDatasources.some((datasource) => datasource.id === currentId)) {
        return currentId;
      }

      return this.availableDatasources[0]?.id || null;
    },
    selectedDatasourceName() {
      if (!this.availableDatasources.length) {
        return 'No datasource available';
      }

      return this.availableDatasources.find((datasource) => datasource.id === this.selectedDatasourceId)?.name
        || this.availableDatasources[0].name;
    },
    canGenerateStory() {
      return Boolean(
        this.selectedIndicator
        && this.selectedLocation
        && this.availablePeriods.length >= 2
        && this.seriesValues.length >= 2,
      );
    },
  },
  watch: {
    isOpen(value) {
      if (value && !this.indicators.length) {
        this.initializeDynamicOptions();
      }
    },
  },
  methods: {
    sortPeriodsByRecency(periods, direction = 'desc') {
      const normalizeValue = (period) => {
        const numeric = Number(period);
        if (!Number.isNaN(numeric)) return numeric;

        const parsedTime = Date.parse(period);
        if (!Number.isNaN(parsedTime)) return parsedTime;

        return String(period).toLowerCase();
      };

      return [...periods].sort((left, right) => {
        const a = normalizeValue(left);
        const b = normalizeValue(right);

        if (typeof a === 'string' || typeof b === 'string') {
          return direction === 'desc'
            ? String(b).localeCompare(String(a), undefined, { numeric: true })
            : String(a).localeCompare(String(b), undefined, { numeric: true });
        }

        return direction === 'desc' ? b - a : a - b;
      });
    },
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    onPluginDisable() {
      this.isEnabled = false;
      this.isOpen = false;
    },
    onPluginEnable() {
      this.isEnabled = true;
    },
    resetResult() {
      this.error = '';
      this.result = null;
    },
    getDatasourceDisplayName(datasource) {
      return datasource?.datasource
        || datasource?.full_name // eslint-disable-line camelcase
        || datasource?.name
        || datasource?.title
        || '';
    },
    getIndicatorDisplayName(indicator) {
      return indicator?.short_name // eslint-disable-line camelcase
        || indicator?.full_name // eslint-disable-line camelcase
        || indicator?.name
        || '';
    },
    async initializeDynamicOptions() {
      if (!this.dashboardId) {
        this.error = 'No active dashboard was found for Trend Storyteller.';
        return;
      }

      this.isLoadingOptions = true;
      this.error = '';

      try {
        const [indicatorResponse, locationResponse, datasourceResponse] = await Promise.all([
          ApiServices.getDashboardIndicator(this.dashboardId),
          ApiServices.fetchAllLocation(),
          ApiServices.fetchAllDataSources(),
        ]);

        this.indicators = Array.isArray(indicatorResponse?.data)
          ? indicatorResponse.data
            .filter((item) => Array.isArray(item.datasources) && item.datasources.length)
            .sort((left, right) => this.getIndicatorDisplayName(left)
              .localeCompare(this.getIndicatorDisplayName(right), undefined, { sensitivity: 'base' }))
          : [];

        let datasourceCatalog = [];
        if (Array.isArray(datasourceResponse?.data?.results)) {
          datasourceCatalog = datasourceResponse.data.results;
        } else if (Array.isArray(datasourceResponse?.data)) {
          datasourceCatalog = datasourceResponse.data;
        }
        this.datasourceCatalog = datasourceCatalog;
        const fetchedLocations = Array.isArray(locationResponse?.data?.results)
          ? locationResponse.data.results
          : [];

        const prioritizedLocations = fetchedLocations.filter((location) => location.level === 1 || location.level === 3);
        this.locations = prioritizedLocations.length ? prioritizedLocations : fetchedLocations;

        if (!this.indicators.length) {
          this.error = 'No dashboard indicators with datasources are available for this dashboard.';
          return;
        }

        this.form.selectedIndicatorId = this.indicators[0].id;
        this.form.selectedLocationId = this.locations.find((location) => location.id === this.defaultLocationId)?.id
          || this.locations[0]?.id
          || null;
        this.form.selectedDatasourceId = this.availableDatasources[0]?.id || null;

        await this.refreshSeries();
      } catch (error) {
        this.error = 'Unable to load dashboard indicators or locations right now.';
        // eslint-disable-next-line no-console
        console.error('Trend Storyteller initialization error:', error);
      } finally {
        this.isLoadingOptions = false;
      }
    },
    async handleIndicatorChange() {
      this.resetResult();
      this.form.selectedDatasourceId = this.availableDatasources[0]?.id || null;
      await this.refreshSeries();
    },
    async handleDatasourceChange() {
      this.resetResult();
      await this.refreshSeries();
    },
    async handleLocationChange() {
      this.resetResult();
      await this.refreshSeries();
    },
    async refreshSeries() {
      this.error = '';
      this.result = null;
      this.availablePeriods = [];
      this.seriesValues = [];

      if (!this.selectedIndicator || !this.selectedLocation || !this.selectedDatasourceId) {
        return;
      }

      this.isLoadingData = true;

      try {
        const periodResponse = await ApiServices.getPeriod({
          indicator: this.selectedIndicator.id,
          datasource: this.selectedDatasourceId,
          location: this.selectedLocation.id,
        });

        const periods = Array.isArray(periodResponse?.data?.periods)
          ? this.sortPeriodsByRecency(periodResponse.data.periods, 'asc')
          : [];

        this.availablePeriods = periods;

        if (periods.length < 2) {
          this.error = 'Not enough periods were found for the selected indicator and location.';
          return;
        }

        const periodResponses = await Promise.all(
          periods.map((period) => ApiServices.getDashboardData(this.dashboardId, {
            indicator: this.selectedIndicator.id,
            datasource: this.selectedDatasourceId,
            location: this.selectedLocation.id,
            period,
          })),
        );

        const values = periodResponses.map((response) => {
          const results = Array.isArray(response?.data?.results) ? response.data.results : [];
          const exactMatch = results.find((item) => Number(item.location) === this.selectedLocation.id);
          const firstNumeric = results.find((item) => !Number.isNaN(Number(item.value)));
          const candidate = exactMatch || firstNumeric;
          return candidate ? Number(candidate.value) : null;
        });

        const pairedSeries = periods
          .map((period, index) => ({
            period,
            value: values[index],
          }))
          .filter((item) => item.value !== null && !Number.isNaN(item.value));

        const displaySeries = this.sortPeriodsByRecency(pairedSeries.map((item) => item.period), 'desc')
          .map((period) => pairedSeries.find((item) => item.period === period))
          .filter(Boolean);

        this.availablePeriods = displaySeries.map((item) => item.period);
        this.seriesValues = displaySeries.map((item) => Number(item.value.toFixed(2)));

        if (this.seriesValues.length < 2) {
          this.error = 'Values could not be derived for enough periods from the selected dashboard context.';
        }
      } catch (error) {
        this.error = 'Unable to load live periods and values for this indicator/location.';
        // eslint-disable-next-line no-console
        console.error('Trend Storyteller series loading error:', error);
      } finally {
        this.isLoadingData = false;
      }
    },
    classifyTrend(values) {
      if (values.length < 2) return 'insufficient';
      const start = values[0];
      const end = values[values.length - 1];
      const deltas = values.slice(1).map((value, index) => value - values[index]);
      const net = end - start;
      const absNet = Math.abs(net);
      const volatility = deltas.reduce((sum, delta) => sum + Math.abs(delta), 0);

      if (absNet <= Math.max(1, Math.abs(start) * 0.03)
        && volatility <= Math.max(2, Math.abs(start) * 0.08)) {
        return 'stable';
      }

      const positive = deltas.filter((delta) => delta > 0).length;
      const negative = deltas.filter((delta) => delta < 0).length;

      if (positive && negative && volatility > absNet * 1.8) {
        return 'volatile';
      }

      if (net > 0) return 'rising';
      if (net < 0) return 'falling';
      return 'stable';
    },
    getRelativeChange(start, end) {
      if (!Number.isFinite(start) || start === 0) {
        return null;
      }

      return ((end - start) / Math.abs(start)) * 100;
    },
    getAverage(values) {
      if (!values.length) return 0;
      return values.reduce((sum, value) => sum + value, 0) / values.length;
    },
    getVolatilityScore(values) {
      if (values.length < 2) return 0;
      const deltas = values.slice(1).map((value, index) => Math.abs(value - values[index]));
      return this.getAverage(deltas);
    },
    describePerformanceBand(changePercent) {
      if (changePercent === null) return 'Change unavailable';
      if (changePercent >= 20) return 'Strong acceleration';
      if (changePercent >= 8) return 'Healthy growth';
      if (changePercent > -8) return 'Holding steady';
      if (changePercent > -20) return 'Soft decline';
      return 'Sharp decline';
    },
    getTrendLabel(trend) {
      const trendMap = {
        rising: 'Rising trend',
        falling: 'Falling trend',
        volatile: 'Volatile trend',
        stable: 'Stable trend',
        insufficient: 'Insufficient data',
      };

      return trendMap[trend] || trendMap.insufficient;
    },
    getIndicatorLabel() {
      return this.getIndicatorDisplayName(this.selectedIndicator) || 'The indicator';
    },
    formatValue(value) {
      const numericValue = Number(value);
      const suffix = this.selectedIndicator?.factor?.['display_factor'] || '';

      if (Number.isNaN(numericValue)) {
        return `${value}${suffix ? ` ${suffix}` : ''}`;
      }

      if (suffix === '%') {
        return `${numericValue.toFixed(1)}%`;
      }

      const formatted = Number.isInteger(numericValue)
        ? numericValue.toLocaleString()
        : numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 });

      return `${formatted}${suffix ? ` ${suffix}` : ''}`;
    },
    buildHeadline(trend, changePercent) {
      const indicatorLabel = this.getIndicatorLabel();
      const locationLabel = this.selectedLocation?.name ? ` in ${this.selectedLocation.name}` : '';
      const changeLabel = changePercent === null ? '' : ` (${changePercent > 0 ? '+' : ''}${changePercent.toFixed(1)}%)`;

      if (trend === 'rising') return `${indicatorLabel}${locationLabel} is gaining momentum${changeLabel}`;
      if (trend === 'falling') return `${indicatorLabel}${locationLabel} is under pressure${changeLabel}`;
      if (trend === 'volatile') return `${indicatorLabel}${locationLabel} is moving unevenly${changeLabel}`;
      return `${indicatorLabel}${locationLabel} is broadly steady${changeLabel}`;
    },
    buildBaseSummary(trend, dates) {
      const indicatorLabel = this.getIndicatorLabel();
      const subject = `${indicatorLabel}${this.selectedLocation ? ` in ${this.selectedLocation.name}` : ''}`;
      const startLabel = dates[0];
      const endLabel = dates[dates.length - 1];

      if (trend === 'rising') {
        if (this.form.audience === 'executive') return `${subject} trended upward overall from ${startLabel} to ${endLabel}.`;
        if (this.form.audience === 'public') return `${subject} went up overall between ${startLabel} and ${endLabel}.`;
        return `${subject} increased overall between ${startLabel} and ${endLabel}.`;
      }
      if (trend === 'falling') {
        if (this.form.audience === 'executive') return `${subject} moved downward overall from ${startLabel} to ${endLabel}.`;
        if (this.form.audience === 'public') return `${subject} went down overall between ${startLabel} and ${endLabel}.`;
        return `${subject} declined overall between ${startLabel} and ${endLabel}.`;
      }
      if (trend === 'volatile') {
        return this.form.audience === 'public'
          ? `${subject} moved up and down across the period without a clear steady direction.`
          : `${subject} fluctuated across the period without a fully consistent direction.`;
      }
      return this.form.audience === 'public'
        ? `${subject} stayed mostly steady between ${startLabel} and ${endLabel}.`
        : `${subject} remained relatively stable between ${startLabel} and ${endLabel}.`;
    },
    buildRecentMomentum(values, dates) {
      const lastIndex = values.length - 1;
      const previousValue = values[lastIndex - 1];
      const latestValue = values[lastIndex];
      const delta = latestValue - previousValue;
      let direction = 'held flat';

      if (delta > 0) direction = 'increased';
      if (delta < 0) direction = 'decreased';

      const magnitude = delta === 0 ? '' : ` by ${this.formatValue(Number(Math.abs(delta).toFixed(2)))}`;

      return `Most recently, the series ${direction}${magnitude} from ${dates[lastIndex - 1]} to ${dates[lastIndex]}.`;
    },
    buildConsistencySignal(trend, values) {
      const volatility = this.getVolatilityScore(values);
      const average = this.getAverage(values);
      const normalizedVolatility = average ? (volatility / Math.abs(average)) * 100 : 0;

      if (trend === 'volatile' || normalizedVolatility >= 15) {
        return 'The pattern is uneven, so single-period gains should be treated cautiously.';
      }

      if (trend === 'stable' || normalizedVolatility <= 5) {
        return 'The pattern is fairly consistent, which makes the latest direction more dependable.';
      }

      return 'The pattern is moderately variable, with movement that is meaningful but not fully smooth.';
    },
    buildWatchout(values, dates, peakIndex, lowIndex) {
      const latestIndex = values.length - 1;

      if (latestIndex === peakIndex) {
        return `The latest period is also the peak so far, which is worth tracking to see if it can be sustained beyond ${dates[latestIndex]}.`;
      }

      if (latestIndex === lowIndex) {
        return `The latest period is the lowest point in the series, making ${dates[latestIndex]} an immediate watchpoint.`;
      }

      return `The gap between the low in ${dates[lowIndex]} and the peak in ${dates[peakIndex]} suggests there is still room for meaningful movement.`;
    },
    generateStory() {
      this.error = '';
      this.result = null;

      if (!this.canGenerateStory) {
        this.error = 'Select a dashboard indicator and location with at least two live periods of data.';
        return;
      }

      const dates = [...this.availablePeriods].reverse();
      const values = [...this.seriesValues].reverse();
      const trend = this.classifyTrend(values);
      const peakValue = Math.max(...values);
      const lowValue = Math.min(...values);
      const peakIndex = values.indexOf(peakValue);
      const lowIndex = values.indexOf(lowValue);
      const deltas = values.slice(1).map((value, index) => ({
        from: dates[index],
        to: dates[index + 1],
        delta: value - values[index],
      }));
      const maxIncrease = deltas.reduce((best, item) => (item.delta > best.delta ? item : best), deltas[0]);
      const maxDrop = deltas.reduce((best, item) => (item.delta < best.delta ? item : best), deltas[0]);
      const averageValue = this.getAverage(values);
      const changePercent = this.getRelativeChange(values[0], values[values.length - 1]);
      const latestValue = values[values.length - 1];
      const trendLabel = this.getTrendLabel(trend);
      const performanceBand = this.describePerformanceBand(changePercent);

      const summary = this.buildBaseSummary(trend, dates);
      const highlights = [
        `Peak value: ${this.formatValue(peakValue)} in ${dates[peakIndex]}.`,
        `Lowest value: ${this.formatValue(lowValue)} in ${dates[lowIndex]}.`,
        `Average value across the selected periods: ${this.formatValue(Number(averageValue.toFixed(2)))}.`,
      ];

      if (maxIncrease.delta > 0) {
        highlights.push(`Largest increase occurred between ${maxIncrease.from} and ${maxIncrease.to} (${this.formatValue(Number(maxIncrease.delta.toFixed(2)))}).`);
      }
      if (maxDrop.delta < 0) {
        highlights.push(`Largest decline occurred between ${maxDrop.from} and ${maxDrop.to} (${this.formatValue(Number(maxDrop.delta.toFixed(2)))}).`);
      }

      const captionMap = {
        rising: 'Overall upward trend.',
        falling: 'Overall downward trend.',
        volatile: 'Mixed movement with noticeable fluctuations.',
        stable: 'Relatively stable trend with minor changes.',
        insufficient: 'Trend summary unavailable.',
      };

      this.result = {
        headline: this.buildHeadline(trend, changePercent),
        keyTakeaway: `Latest value is ${this.formatValue(latestValue)} in ${dates[dates.length - 1]}, compared with ${this.formatValue(values[0])} in ${dates[0]}.`,
        summary,
        highlights,
        trend,
        trendLabel,
        performanceBand,
        recentMomentum: this.buildRecentMomentum(values, dates),
        consistencySignal: this.buildConsistencySignal(trend, values),
        watchout: this.buildWatchout(values, dates, peakIndex, lowIndex),
        insightCards: [
          {
            title: 'Latest reading',
            value: this.formatValue(latestValue),
            copy: `Recorded in ${dates[dates.length - 1]}.`,
          },
          {
            title: 'Net change',
            value: changePercent === null
              ? 'Unavailable'
              : `${changePercent > 0 ? '+' : ''}${changePercent.toFixed(1)}%`,
            copy: `From ${dates[0]} to ${dates[dates.length - 1]}.`,
          },
          {
            title: 'Series range',
            value: this.formatValue(Number((peakValue - lowValue).toFixed(2))),
            copy: 'Difference between the lowest and highest points.',
          },
        ],
        caption: captionMap[trend] || captionMap.insufficient,
      };
    },
  },
};
</script>

<style scoped lang="scss">
.trend-storyteller-shell {
  position: fixed;
  right: 1.5rem;
  bottom: 1.5rem;
  z-index: 9999;
}

.trend-storyteller-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  border: none;
  border-radius: 999px;
  padding: 0.82rem 1rem;
  background: linear-gradient(135deg, rgba(18, 80, 66, 0.98), rgba(26, 132, 109, 0.95));
  color: #fff;
  box-shadow: 0 16px 34px rgba(15, 78, 65, 0.28);
  cursor: pointer;
}

.trend-storyteller-trigger-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.trend-storyteller-trigger-text {
  font-size: 0.86rem;
  font-weight: 700;
}

.trend-storyteller-panel {
  position: absolute;
  right: 0;
  bottom: calc(100% + 0.9rem);
  width: min(34rem, calc(100vw - 2rem));
  max-height: min(80vh, 46rem);
  overflow-y: auto;
  padding: 1.25rem;
  border-radius: 1.4rem;
  background: linear-gradient(160deg, rgba(252, 255, 253, 0.98), rgba(243, 249, 247, 0.96));
  border: 1px solid rgba(18, 80, 66, 0.1);
  box-shadow: 0 24px 50px rgba(17, 55, 47, 0.18);
}

.trend-storyteller-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.trend-storyteller-label {
  margin: 0 0 0.22rem;
  color: #197760;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.trend-storyteller-header h3 {
  margin: 0;
  color: #163a32;
}

.trend-storyteller-subtitle {
  margin: 0.45rem 0 0;
  color: #5a6d67;
  font-size: 0.86rem;
  line-height: 1.6;
}

.trend-storyteller-close {
  width: 2.25rem;
  height: 2.25rem;
  border: none;
  border-radius: 999px;
  background: rgba(18, 80, 66, 0.09);
  color: #2f5249;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
}

.trend-storyteller-close-icon {
  width: 0.9rem;
  height: 0.9rem;
  display: block;
  stroke: currentColor;
  stroke-width: 2.15;
  stroke-linecap: round;
  fill: none;
}

.trend-storyteller-close:hover {
  background: rgba(18, 80, 66, 0.16);
  color: #173a33;
  transform: translateY(-1px);
}

.trend-storyteller-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
  margin-top: 1rem;
}

.trend-storyteller-preview-grid {
  margin-top: 0.75rem;
}

.trend-storyteller-field {
  display: flex;
  flex-direction: column;
  gap: 0.38rem;
}

.trend-storyteller-field span {
  color: #314c45;
  font-size: 0.78rem;
  font-weight: 700;
}

.trend-storyteller-field input,
.trend-storyteller-field select,
.trend-storyteller-field textarea {
  width: 100%;
  border: 1px solid rgba(18, 80, 66, 0.12);
  border-radius: 0.9rem;
  padding: 0.78rem 0.88rem;
  background: rgba(255, 255, 255, 0.86);
  color: #1f2c29;
  font-size: 0.88rem;
}

.trend-storyteller-field input:disabled,
.trend-storyteller-field textarea:disabled {
  color: #52635e;
  background: rgba(244, 248, 246, 0.9);
}

.trend-storyteller-field textarea {
  resize: vertical;
  min-height: 4.8rem;
}

.trend-storyteller-field-wide {
  grid-column: 1 / -1;
}

.trend-storyteller-status {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1rem;
}

.trend-storyteller-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.7rem;
  margin-top: 1rem;
}

.trend-storyteller-btn {
  border: none;
  border-radius: 999px;
  padding: 0.78rem 1rem;
  background: linear-gradient(135deg, #176b58, #1d9c80);
  color: white;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
}

.trend-storyteller-btn.secondary {
  background: rgba(18, 80, 66, 0.08);
  color: #31544b;
}

.trend-storyteller-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.trend-storyteller-error {
  margin: 0.85rem 0 0;
  color: #c0392b;
  font-size: 0.82rem;
  font-weight: 600;
}

.trend-storyteller-result {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 1.1rem;
  background: rgba(18, 80, 66, 0.05);
}

.trend-storyteller-hero {
  padding: 1rem;
  border-radius: 1rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(219, 242, 235, 0.8));
  border: 1px solid rgba(29, 156, 128, 0.14);
}

.trend-storyteller-hero h4 {
  margin: 0;
  color: #143a32;
  font-size: 1.05rem;
  line-height: 1.4;
}

.trend-storyteller-insight-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.9rem;
}

.trend-storyteller-insight-card {
  padding: 0.9rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(18, 80, 66, 0.08);
}

.trend-storyteller-insight-value {
  margin: 0;
  color: #18493e;
  font-size: 1rem;
  font-weight: 800;
}

.trend-storyteller-insight-copy {
  margin: 0.3rem 0 0;
  color: #59706a;
  font-size: 0.8rem;
  line-height: 1.55;
}

.trend-storyteller-result-block + .trend-storyteller-result-block {
  margin-top: 0.9rem;
}

.trend-storyteller-result-label {
  margin: 0 0 0.35rem;
  color: #197760;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.trend-storyteller-result-text,
.trend-storyteller-highlights {
  margin: 0;
  color: #1d2926;
  line-height: 1.7;
}

.trend-storyteller-highlights {
  padding-left: 1.2rem;
}

.trend-storyteller-result-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1rem;
}

.trend-storyteller-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.5rem 0.78rem;
  background: rgba(29, 156, 128, 0.14);
  color: #126451;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: capitalize;
}

.trend-storyteller-chip.muted {
  background: rgba(18, 80, 66, 0.08);
  color: #48615a;
  text-transform: none;
}

.trend-storyteller-fade-enter-active,
.trend-storyteller-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.25s ease;
}

.trend-storyteller-fade-enter,
.trend-storyteller-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 768px) {
  .trend-storyteller-shell {
    right: 1rem;
    left: 1rem;
    bottom: 1rem;
  }

  .trend-storyteller-trigger {
    width: 100%;
    justify-content: center;
  }

  .trend-storyteller-panel {
    left: 0;
    right: 0;
    width: auto;
    bottom: calc(100% + 0.75rem);
  }

  .trend-storyteller-grid {
    grid-template-columns: 1fr;
  }

  .trend-storyteller-insight-grid {
    grid-template-columns: 1fr;
  }
}
</style>
